import {formatSlaDuration} from '../../tickets/utils/slaPresentation';

/**
 * How the dashboard's rollups read.
 *
 * The one place a rollup is formatted: the table's totals row and every group
 * header show the same three (of five) figures over different row sets, so they
 * must round once, here, or two numbers over the same tickets end up disagreeing by
 * a percentage point. `groupStatsFormatting` delegates to this module for exactly
 * that reason.
 */

/** An absent rollup — nothing in scope to compute it from. Matches the table's empty-cell dash. */
const NO_VALUE = '—';

/** A 0–1 ratio as a whole percentage. Null reads as a dash, not `0%`. */
export function formatPct(ratio: number | null): string {
	if (ratio == null) return NO_VALUE;
	return `${Math.round(ratio * 100)}%`;
}

/**
 * A 0–100 percentage as whole percent. Null reads as a dash, not `0%`.
 *
 * Sibling to `formatPct` for values that already arrive in percentage points —
 * routing them through `formatPct` would need a divide-by-100 first, which is
 * both an obvious place to introduce a 100× bug and a chance to land on the
 * wrong side of a `.5` rounding boundary. This formatter rounds the same
 * quantity the source rounded to (`Math.round` on the raw percentage), so the
 * bar's whole percent is bit-identical to the gauge's.
 */
export function formatPercentagePoints(pct: number | null): string {
	if (pct == null) return NO_VALUE;
	return `${Math.round(pct)}%`;
}

export function formatTotalCount(total: number): string {
	return String(total);
}

/** Seconds in the ticketing tree's compact duration form — `2d 3h`, `4h 30m`, `12m`. */
export function formatMedianDuration(seconds: number | null): string {
	if (seconds == null) return NO_VALUE;
	return formatSlaDuration(seconds);
}
